// Where the tables of a multi-table level stand on the field.
//
// A layout is authored as a FORMATION — normalized anchor points in [-1, 1],
// u across the frame (+ right) and v in depth (+ towards the camera) — never as
// world coordinates. The formation says only what SHAPE the tables make; how far
// apart they actually end up is solved here from the real size of each table, so
// a trapezoid of four small tables and a trapezoid of four wide ones both come
// out tight but never overlapping.
//
// Solving one scalar `s` (the formation's scale) instead of hand-placing tables
// is what keeps this correct for mixed table sizes: for every pair, the minimal
// scale that separates them is a closed form, and the formation's scale is the
// largest of those. See solveScale().

import { LevelLayer } from "../../../level_config";

export interface TableClaim
{
  // Half-extents of the tabletop along X and Z.
  half_width: number;
  half_depth: number;
  // Radius the tabletop sweeps when the field rotates, measured from the table's
  // pivot. Only consulted when `rotates` is set.
  radius: number;
  rotates: boolean;
  // Absolute tabletop size PhysicsTable must stretch to.
  target_width: number;
  target_depth: number;
}

export interface TableFit  
{
  layers: LevelLayer[];
  claim: TableClaim;
  cols_limit: number;
  layer_limit: number;
  rotation?: { angle?: number; spin?: number };
}

export interface Size3
{
  x: number;
  y: number;
  z: number;
}

export interface Overhang
{
  x: number;
  z: number;
}

// Ratio of the gap between grid cells to a cube's size — mirrors
// PhysicsCube.GRID_GAP_RATIO.
const GRID_GAP_RATIO = 0.02;

const TABLE_EDGE_INSET = 0.09;

// Mirrors MAX_TABLE_LAYERS in level_config.ts. Kept local so this module stays
// portable — the editor holds a copy of it and must not import game code.
const MAX_TABLE_LAYERS = 10;

/**
 * The measurements the LEVEL EDITOR assumes when it authors a layout — and ONLY
 * those. The client always uses what it measured off the loaded models; these
 * exist so modelSizeDrift() can shout when an art change makes the editor's
 * previews and its authored slots stop describing the real game.
 *
 * Deliberately narrower than "the model sizes":
 *  - cube.y is the block height, which no table footprint depends on;
 *  - base_top.x/y never reach the editor either. A table's WIDTH comes from the
 *    tower standing on it (columns × grid step), and the client then stretches
 *    the shipped tabletop to that width using its own measured base_top.x. A
 *    wider default tabletop therefore changes nothing upstream.
 * Checking them anyway would raise the alarm on art changes that cannot affect a
 * single generated level, which is the fastest way to teach everyone to ignore
 * this warning.
 *
 * Keep in sync with FIELD_METRICS in the editor's fieldMetrics.ts.
 */
export const EDITOR_ASSUMPTIONS = {
  /** Grid step along X and Z, and the width a table adds for its rims. */
  cube_x: 0.6,
  cube_z: 0.6,
  /** Depth of an unstretched tabletop, checked only by modelSizeDrift(). */
  base_top_z: 1.3891,
};

/** Non-empty when the loaded models no longer match what the editor assumes. */
export function modelSizeDrift( cube: Size3, base_top: Size3 ): string[]
{
  const TOLERANCE = 1e-3;
  const issues: string[] = [];

  const compare = ( name: string, actual: number, expected: number ) =>
  {
    if ( Math.abs( actual - expected ) > TOLERANCE )
      issues.push(
        `${name} is ${actual.toFixed( 4 )}, the editor assumes ${expected}` +
        ` — update EDITOR_ASSUMPTIONS here and fieldMetrics.ts in the editor, then re-check multi-table levels`
      );
  };

  compare( "cube.x", cube.x, EDITOR_ASSUMPTIONS.cube_x );
  compare( "cube.z", cube.z, EDITOR_ASSUMPTIONS.cube_z );
  compare( "base_top.z", base_top.z, EDITOR_ASSUMPTIONS.base_top_z );

  return issues;
}

export function tableClaim(
  layers: LevelLayer[],
  cols_limit: number,
  rotates: boolean,
  cube: Size3,
  tilt_rad = 0,
  // Fractions of the size measured below — see TableConfig.topScale. Kept as a
  // plain shape rather than the level type so this module stays importable by
  // the editor, which holds a copy of it and must not import game code.
  top_scale?: { width?: number; depth?: number },

  slide_distance = 0,
  min_top?: { width: number; depth: number }
): TableClaim
{
  const step_x = cube.x * ( 1 + GRID_GAP_RATIO );
  const step_z = cube.z * ( 1 + GRID_GAP_RATIO );

  const effective_layers = layers.slice( 0, MAX_TABLE_LAYERS );

  // No layer carries a real layout (a level with no server/bundled config —
  // createRandomTowers() takes over next and places blocks assuming a
  // full-size table) — don't shrink the table down to a single column.
  const max_cols = effective_layers.length === 0
    ? cols_limit
    : Math.min(
        cols_limit,
        Math.max(
          ...effective_layers.map( l => l.grid[0]?.length ?? 0 ),
          1
        )
      );

  let min_offset_z: number | undefined;
  let max_offset_z: number | undefined;

  for ( const layer of effective_layers )
  {
    for ( const piece of layer.pieces ?? [] )
    {
      const offset_z = piece.offsetZ ?? 0;

      min_offset_z = min_offset_z === undefined ? offset_z : Math.min( min_offset_z, offset_z );
      max_offset_z = max_offset_z === undefined ? offset_z : Math.max( max_offset_z, offset_z );
    }
  }

  const offset_spread = min_offset_z !== undefined && max_offset_z !== undefined ? max_offset_z - min_offset_z : 0;

  // A tilted table carries its stack leaning, and a leaning stack is WIDER than
  // its footprint: the top row's downhill corner sits content_height * tan(tilt)
  // past the bottom row's. Sizing the tabletop off the columns alone would leave
  // that corner hanging over thin air, so pay for the lean on both ends — the
  // uphill end needs the same slack, because the top row leans off it inwards
  // and the tabletop would otherwise stop short of the silhouette there.
  const rows = effective_layers.length === 0
    ? 0
    : Math.max( ...effective_layers.map( l => l.grid.length ), 0 );
  const lean = rows * cube.y * Math.tan( Math.abs( tilt_rad ) );

  const top_width = Math.max(
    ( max_cols * step_x - 2 * TABLE_EDGE_INSET + 2 * lean ) * ( top_scale?.width ?? 1 ),
    step_x * 0.5,
    min_top?.width ?? 0
  );
  const top_depth = Math.max(
    ( ( effective_layers.length + offset_spread ) * step_z - 2 * TABLE_EDGE_INSET )
      * ( top_scale?.depth ?? 1 ),
    step_z * 0.5,
    min_top?.depth ?? 0
  );

  return {
    half_width: top_width * 0.5 + slide_distance,
    half_depth: top_depth * 0.5,
    radius: Math.hypot( top_width + slide_distance * 2, top_depth ) * 0.5,
    rotates,
    target_width: top_width,
    target_depth: top_depth,
  };
}

export function blockOverhang( cube: Size3 ): Overhang
{
  return {
    x: TABLE_EDGE_INSET - cube.x * GRID_GAP_RATIO * 0.5,
    z: TABLE_EDGE_INSET - cube.z * GRID_GAP_RATIO * 0.5,
  };
}

export function towerTopHeight(
  layers: LevelLayer[],
  cols_limit: number,
  layer_limit: number
): number
{
  const effective_layers = layers.slice( 0, layer_limit );
  if ( effective_layers.length === 0 )
    return 0;

  const max_cols = Math.min(
    cols_limit,
    Math.max( ...effective_layers.map( l => l.grid[0]?.length ?? 0 ) )
  );

  let top = 0;

  for ( const layer of effective_layers )
  {
    const rows = layer.grid.length;

    for ( const piece of layer.pieces ?? [] )
    {
      if ( piece.x >= max_cols )
        continue;

      top = Math.max( top, rows - piece.y + ( piece.offsetY ?? 0 ) );
    }
  }

  return top;
}

export interface FormationPoint
{
  u: number;
  v: number;
}

export interface Formation
{
  id: string;
  points: FormationPoint[];
  // Squashes or stretches the shape along the depth axis before solving. Depth
  // is nearly free on a portrait screen (the camera looks almost straight on, so
  // z barely costs frame width), while spreading along u pushes the camera back —
  // so most shapes are authored wider than deep and pulled in with this.
  depth_scale?: number;
}

export interface Placement
{
  x: number;
  z: number;
}

export interface FieldLayout
{
  formation_id: string;
  placements: Placement[];
  // Half-width the camera must fit, and the z of whatever reaches closest to it.
  required_half_width: number;
  nearest_z: number;
}

// Clearance kept between two tabletops. Sized so a block knocked off one table
// lands on the ground rather than on its neighbour's top.
export const TABLE_GAP = 1.3;

// Authored shapes, grouped by how many tables they place. Depth (z) is
// centred on the front pair by the solver afterwards; x is NOT re-centred
// (see resolveFieldLayout's centre()) — it is whatever compact() resolves
// from the anchors as authored, so keep u symmetric around 0 if the shape
// should sit in the middle of the frame.
const FORMATIONS: Record<number, Formation[]> = {
  2: [
    { id: "pair", points: [ { u: -1, v: 0 }, { u: 1, v: 0 } ] },
    { id: "diagonal", points: [ { u: -1, v: -1 }, { u: 1, v: 1 } ], depth_scale: 0.6 },
    { id: "offset-column", points: [ { u: -0.45, v: 1 }, { u: 0.45, v: -1 } ], depth_scale: 1 },
    // Straight back, no side-by-side pair — costs the frame only one table's
    // width no matter how wide the cast is. The guaranteed-to-fit shape the
    // wider ones can fall back to now that the camera no longer pulls back.
    { id: "column-2", points: [ { u: 0, v: 0.2 }, { u: 0, v: -0.2 } ] },
  ],
  3: [
    // Two in front, one behind the gap between them — the classic wedge.
    { id: "wedge", points: [ { u: -1, v: 1 }, { u: 1, v: 1 }, { u: 0, v: -1 } ], depth_scale: 0.75 },
    // Inverted: one forward, two flanking behind. Reads as a "bow".
    { id: "arrow", points: [ { u: 0, v: 1 }, { u: -1, v: -1 }, { u: 1, v: -1 } ], depth_scale: 0.75 },
    // Descending diagonal — no two tables share a depth band.
    { id: "staircase", points: [ { u: -1, v: 1 }, { u: 0, v: 0 }, { u: 1, v: -1 } ], depth_scale: 0.7 },
    // Shallow arc facing the player.
    { id: "arc", points: [ { u: -1, v: 0.35 }, { u: 0, v: 1 }, { u: 1, v: 0.35 } ], depth_scale: 0.8 },
    // Straight back, no side-by-side pair at any depth — the 3-table guaranteed-to-fit shape.
    { id: "column-3", points: [ { u: 0, v: 0.3 }, { u: 0, v: 0 }, { u: 0, v: -0.3 } ] },
  ],
  4: [
    // Wide at the back, narrow at the front: the back row stays visible past the
    // shoulders of the front one instead of hiding behind it.
    { id: "trapezoid", points: [ { u: -1, v: -1 }, { u: 1, v: -1 }, { u: -0.5, v: 1 }, { u: 0.5, v: 1 } ], depth_scale: 0.7 },
    // The same the other way round — a wide front rank and a tucked-in back pair.
    { id: "trapezoid-inverted", points: [ { u: -0.5, v: -1 }, { u: 0.5, v: -1 }, { u: -1, v: 1 }, { u: 1, v: 1 } ], depth_scale: 0.7 },
    { id: "diamond", points: [ { u: 0, v: 1 }, { u: -1, v: 0 }, { u: 1, v: 0 }, { u: 0, v: -1 } ], depth_scale: 0.9 },
    // Zig-zag along the frame: every neighbour alternates near/far.
    { id: "zigzag", points: [ { u: -1, v: 0.8 }, { u: -0.33, v: -0.8 }, { u: 0.33, v: 0.8 }, { u: 1, v: -0.8 } ], depth_scale: 0.55 },
    { id: "chevron", points: [ { u: -1, v: -1 }, { u: -0.35, v: 0.5 }, { u: 0.35, v: 0.5 }, { u: 1, v: -1 } ], depth_scale: 0.7 },
    { id: "square", points: [ { u: -1, v: 1 }, { u: 1, v: 1 }, { u: -1, v: -1 }, { u: 1, v: -1 } ], depth_scale: 0.8 },
    // Straight back, no side-by-side pair at any depth — the 4-table guaranteed-to-fit shape.
    { id: "column-4", points: [ { u: 0, v: 0.45 }, { u: 0, v: 0.15 }, { u: 0, v: -0.15 }, { u: 0, v: -0.45 } ] },
  ],
};

function mulberry32( seed: number ): () => number
{
  let a = seed >>> 0;

  return () =>
  {
    a = ( a + 0x6d2b79f5 ) >>> 0;
    let t = Math.imul( a ^ ( a >>> 15 ), 1 | a );
    t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
    return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
  };
}

// Dart-throwing in normalized space: keeps sampling until the points are spread
// out enough to read as a deliberate shape rather than a clump. The solver still
// guarantees non-overlap afterwards — this only governs how the shape LOOKS.
function randomFormation( count: number, seed: number ): Formation
{
  const MIN_SEPARATION = 0.75;
  const rng = mulberry32( seed );
  const points: FormationPoint[] = [];

  for ( let attempt = 0; attempt < 400 && points.length < count; attempt++ )
  {
    const candidate = { u: rng() * 2 - 1, v: rng() * 2 - 1 };

    if ( points.every( p => Math.hypot( p.u - candidate.u, p.v - candidate.v ) >= MIN_SEPARATION ) )
      points.push( candidate );
  }

  // Ran out of attempts (possible for 4 points at this separation): fall back to
  // an authored shape rather than shipping a clumped one.
  if ( points.length < count )
    return FORMATIONS[count][ Math.floor( rng() * FORMATIONS[count].length ) ];

  return { id: `random-${seed}`, points, depth_scale: 0.7 };
}

function greatestCommonDivisor( a: number, b: number ): number
{
  return b === 0 ? a : greatestCommonDivisor( b, a % b );
}

// Largest stride coprime with `length`, so stepping by it visits every shape
// before repeating any — and, since the stride is never a multiple of the length,
// never lands on the same shape twice in a row. A plain hash-per-level cannot
// promise either: it repeats by chance, and "shift by one on a collision" only
// moves the collision to the level after.
function shuffleStride( length: number ): number
{
  for ( let stride = length - 1; stride > 1; stride-- )
    if ( greatestCommonDivisor( stride, length ) === 1 )
      return stride;

  return 1;
}

/**
 * The formation for a level: stable across restarts (derived from the level
 * number alone) and never the same shape two levels running. Every fifth level
 * draws a random shape instead of an authored one.
 */
export function pickFormation( level: number, count: number ): Formation
{
  const authored = FORMATIONS[count];
  if ( !authored || authored.length === 0 )
    return { id: "row", points: Array.from( { length: count }, ( _, i ) => ( { u: i * 2 / Math.max( 1, count - 1 ) - 1, v: 0 } ) ) };

  if ( level % 5 === 0 )
    return randomFormation( count, level * 2654435761 + count );

  const index = ( level * shuffleStride( authored.length ) + count ) % authored.length;

  return authored[index];
}

/**
 * Smallest scale factor that keeps two tables clear of each other, given the
 * normalized offset between their anchors.
 *
 * Axis-aligned tables only need ONE axis to separate them, so the answer is the
 * cheapest of "push apart until the X gaps clear", "…until the Z gaps clear"
 * and "…until they only meet corner to corner" — that is what lets a
 * trapezoid stay compact instead of ballooning to the width its widest pair
 * would need. A rotating table sweeps a disc, so any pair involving one falls
 * back to a radius test, which needs real distance.
 */
function solvePairScale( a: TableClaim, b: TableClaim, du: number, dv: number ): number
{
  if ( a.rotates || b.rotates )
  {
    const distance = Math.hypot( du, dv );
    return distance > 1e-6 ? ( a.radius + b.radius + TABLE_GAP ) / distance : Infinity;
  }

  const along = ( reach: number, d: number ) => Math.abs( d ) > 1e-6 ? reach / Math.abs( d ) : Infinity;

  const width = a.half_width + b.half_width;
  const depth = a.half_depth + b.half_depth;

  const by_x = along( width + TABLE_GAP, du );
  const by_z = along( depth + TABLE_GAP, dv );
  const by_corner = Math.max( along( width, du ), along( depth, dv ) );

  return Math.min( by_x, by_z, by_corner );
}

// solvePairScale() places the tightest pair at EXACTLY the clearance distance, so
// this test has to tolerate touching — without the epsilon, the accumulated error
// of that multiplication reads the solver's own output back as an overlap.
const TOUCH_EPSILON = 1e-6;

function overlaps( a: TableClaim, b: TableClaim, ap: Placement, bp: Placement ): boolean
{
  const dx = Math.abs( ap.x - bp.x );
  const dz = Math.abs( ap.z - bp.z );

  if ( a.rotates || b.rotates )
    return Math.hypot( dx, dz ) < a.radius + b.radius + TABLE_GAP - TOUCH_EPSILON;

  const width = a.half_width + b.half_width - TOUCH_EPSILON;
  const depth = a.half_depth + b.half_depth - TOUCH_EPSILON;

  return ( dx < width + TABLE_GAP && dz < depth )
    || ( dx < width && dz < depth + TABLE_GAP );
}

// The solved scale is driven by the single tightest pair, which leaves smaller
// tables further out than they need to be. Pull each one back towards the centre
// along its own anchor direction, as far as its neighbours allow.
//
// Every table is measured against the SAME snapshot and they all move together
// (Jacobi, not Gauss-Seidel). Relaxing them one at a time would be simpler and
// converge tighter, but it makes the result depend on table order: in a mirror-
// symmetric shape with identical tables, whichever side is visited first gets
// pulled in further and the shape comes out visibly lopsided. Moving in lockstep
// maps equal tables to equal factors, so a symmetric shape stays symmetric.
//
// Simultaneous moves can overshoot into each other, so each round is verified as
// a whole and dropped if it collides — the previous round is always valid.
function compact( points: FormationPoint[], claims: TableClaim[], scale: number ): Placement[] {
  let factors = points.map( () => 1 );

  const at = ( i: number, factor: number ): Placement => ( {
    x: points[i].u * scale * factor,
    z: points[i].v * scale * factor,
  } );

  const placementsFor = ( fs: number[] ) => points.map( ( _, i ) => at( i, fs[i] ) );

  const anyOverlap = ( spots: Placement[] ): boolean =>
  {
    for ( let i = 0; i < spots.length; i++ )
      for ( let j = i + 1; j < spots.length; j++ )
        if ( overlaps( claims[i], claims[j], spots[i], spots[j] ) )
          return true;

    return false;
  };

  const minimalFactor = ( i: number, snapshot: number[] ): number =>
  {
    const clears = ( factor: number ): boolean =>
    {
      const candidate = at( i, factor );

      for ( let j = 0; j < points.length; j++ )
        if ( j !== i && overlaps( claims[i], claims[j], candidate, at( j, snapshot[j] ) ) )
          return false;

      return true;
    };

    if ( !clears( 0 ) )
      return snapshot[i];

    let lo = 0;
    let hi = snapshot[i];

    for ( let step = 0; step < 14; step++ )
    {
      const mid = ( lo + hi ) * 0.5;
      if ( clears( mid ) )
        hi = mid;
      else
        lo = mid;
    }

    return hi;
  };

  for ( let pass = 0; pass < 6; pass++ )
  {
    const next = factors.map( ( _, i ) => minimalFactor( i, factors ) );

    if ( anyOverlap( placementsFor( next ) ) )
      break;

    const moved = Math.max( ...next.map( ( f, i ) => Math.abs( f - factors[i] ) ) );
    factors = next;

    if ( moved < 1e-4 )
      break;
  }

  return placementsFor( factors );
}

const SQUEEZE_BISECTIONS = 20;

function squeezeToWidth<T extends { centred: Placement[]; required_half_width: number }>(
  spots: Placement[],
  claims: TableClaim[],
  max_half_width: number,
  centre: ( spots: Placement[] ) => T
): T
{
  const squeezed = ( factor: number ) => centre( spots.map( p => ( { x: p.x * factor, z: p.z } ) ) );

  const clear = ( factor: number ): boolean =>
  {
    const candidate = squeezed( factor ).centred;

    for ( let i = 0; i < candidate.length; i++ )
      for ( let j = i + 1; j < candidate.length; j++ )
        if ( overlaps( claims[i], claims[j], candidate[i], candidate[j] ) )
          return false;

    return true;
  };

  let fitting = 0;
  let too_wide = 1;

  for ( let step = 0; step < SQUEEZE_BISECTIONS; step++ )
  {
    const mid = ( fitting + too_wide ) * 0.5;

    if ( squeezed( mid ).required_half_width <= max_half_width )
      fitting = mid;
    else
      too_wide = mid;
  }

  if ( clear( fitting ) )
    return squeezed( fitting );

  let overlapping = fitting;
  let clears = 1;

  for ( let step = 0; step < SQUEEZE_BISECTIONS; step++ )
  {
    const mid = ( overlapping + clears ) * 0.5;

    if ( clear( mid ) )
      clears = mid;
    else
      overlapping = mid;
  }

  return squeezed( clears );
}

/**
 * Resolves a formation into table positions relative to the field centre.
 * Guarantees no two tabletops (or swept discs, for rotating tables) come closer
 * than TABLE_GAP, and centres the result on its own bounding box so the shape
 * sits in the middle of the frame whatever its anchors were.
 *
 * `max_half_width`, when given, is a last resort: if the natural result still
 * reaches past it, the shape is squeezed towards the centre along X alone (see
 * squeezeToWidth) and re-centred, spending whatever spare Z clearance it has
 * before giving up on fitting the frame.
 */
export function resolveFieldLayout( formation: Formation, claims: TableClaim[], max_half_width?: number ): FieldLayout
{
  const depth_scale = formation.depth_scale ?? 1;
  const points = formation.points
    .slice( 0, claims.length )
    .map( p => ( { u: p.u, v: p.v * depth_scale } ) );

  let scale = 0;

  for ( let i = 0; i < points.length; i++ )
    for ( let j = i + 1; j < points.length; j++ )
    {
      const pair_scale = solvePairScale(
        claims[i],
        claims[j],
        points[i].u - points[j].u,
        points[i].v - points[j].v
      );

      if ( pair_scale !== Infinity )
        scale = Math.max( scale, pair_scale );
    }

  const placements = compact( points, claims, scale );

  const reach = ( i: number ) => claims[i].rotates
    ? { x: claims[i].radius, z: claims[i].radius }
    : { x: claims[i].half_width, z: claims[i].half_depth };

  const bbox = ( spots: Placement[], indices: number[] ) =>
  {
    let min_x = Infinity;
    let max_x = -Infinity;
    let min_z = Infinity;
    let max_z = -Infinity;

    for ( const i of indices )
    {
      const p = spots[i];
      const r = reach( i );
      min_x = Math.min( min_x, p.x - r.x );
      max_x = Math.max( max_x, p.x + r.x );
      min_z = Math.min( min_z, p.z - r.z );
      max_z = Math.max( max_z, p.z + r.z );
    }

    return { min_x, max_x, min_z, max_z };
  };

  const all_indices = placements.map( ( _, i ) => i );

  // Depth (z) is centred on the two FRONT tables (closest to the camera, i.e.
  // largest v) instead of the whole formation's bounding box — otherwise a
  // table placed behind the main pair silently drags that pair off-centre in
  // depth. Any further tables still push off from that anchored front pair via
  // the collision solver above; they just don't get a say in where the front
  // pair sits.
  //
  // x is NOT centred this way — it is left exactly as compact() resolved it.
  // Centring x on the front pair used to cause the opposite problem: on a
  // shape like "staircase", where no two anchors share a depth (so "the front
  // pair" is really just its two most-forward points, not a matched pair),
  // that pair is not left-right symmetric on its own, and forcing it to be
  // dragged the WHOLE shape off to one side — wasting frame width the raw,
  // unshifted placement didn't need. compact() already keeps a shape centred
  // on its own whenever the authored anchors are u-symmetric and the claims
  // are equal; skipping the x shift here just stops "fixing" what wasn't
  // broken and started breaking staircase instead. Keep this in step with
  // resolveFieldLayout's centre() in the editor's fieldLayout.ts.
  //
  // Ties at the cutoff are kept together rather than broken by index order —
  // a shape like "diamond" has two tables at the same depth for 2nd place,
  // and picking only one of them would silently break its left-right mirror.
  const TIE_EPSILON = 1e-9;
  const by_depth = all_indices.slice().sort( ( a, b ) => points[b].v - points[a].v );
  const cutoff = points[ by_depth[ Math.min( 1, by_depth.length - 1 ) ] ].v;
  const front_indices = all_indices.filter( i => points[i].v >= cutoff - TIE_EPSILON );

  const centre = ( spots: Placement[] ) =>
  {
    const front_box = bbox( spots, front_indices );
    const shift_z = ( front_box.min_z + front_box.max_z ) * 0.5;
    const centred = spots.map( p => ( { x: p.x, z: p.z - shift_z } ) );
    const full_box = bbox( centred, all_indices );

    return {
      centred,
      required_half_width: Math.max( full_box.max_x, -full_box.min_x ),
      nearest_z: Math.max( ...centred.map( ( p, i ) => p.z + reach( i ).z ) ),
    };
  };

  let result = centre( placements );

  if ( max_half_width !== undefined && result.required_half_width > max_half_width )
    result = squeezeToWidth( result.centred, claims, max_half_width, centre );

  return {
    formation_id: formation.id,
    placements: result.centred,
    required_half_width: result.required_half_width,
    nearest_z: result.nearest_z,
  };
}

// How far past the frame a shape may reach before it is rejected. The camera no
// longer pulls back to compensate for an oversized formation (that pushback was
// removed), so a shape wider than the frame simply runs off-screen forever —
// there used to be slack here (1.35) when the camera could still absorb it, but
// now the budget has to be the real frame width. Only matters for levels
// authored before slots existed, which fall through to the local library below;
// slotted levels are already sized correctly by the editor's own copy of this
// constant.
const MAX_FRAME_OVERFLOW = 1;

// Depth multipliers tried on a shape before giving up on it, narrowest frame
// cost last. 1 is the shape exactly as authored.
const DEPTH_STRETCHES = [ 1, 1.45, 2, 3 ];
const MIN_NARROWING = 0.05;

/**
 * Picks the shape for a level AND resolves it, skipping shapes that would cost
 * too much frame at this table size. Selection has to be claim-aware: the same
 * anchors spread twice as wide for twice-as-wide tables, so whether a shape is
 * affordable is not a property of the shape alone.
 *
 * `slots` are the anchors the level editor's generator authored for this level.
 * When present they ARE the shape — the client only resolves them to metres and,
 * if they do not fit this particular screen, squeezes them across and
 * stretches them in depth. Only levels authored before slots existed fall
 * through to the local library, which is why FORMATIONS still ships here.
 *
 * Falls back to the narrowest candidate when nothing is affordable, so this
 * always returns a layout.
 */
export function chooseFieldLayout(
  level: number,
  claims: TableClaim[],
  frame_half_width: number,
  slots?: ( FormationPoint | undefined )[]
): FieldLayout
{
  const budget = frame_half_width * MAX_FRAME_OVERFLOW;
  const authored_slots = slots?.slice( 0, claims.length );
  const candidates: Formation[] = [];

  if ( authored_slots && authored_slots.length === claims.length && authored_slots.every( s => s !== undefined ) )
  {
    // Authored shapes are final. If one turns out expensive on this screen it
    // gets stretched in depth and, past that, the camera pulls back — but it is
    // never swapped for a different shape, because the editor picked and
    // previewed THIS one.
    candidates.push( { id: "authored", points: authored_slots as FormationPoint[] } );
  }
  else
  {
    const library = FORMATIONS[claims.length] ?? [];
    candidates.push( pickFormation( level, claims.length ) );

    for ( let i = 0; i < library.length; i++ )
      candidates.push( library[ ( level + i ) % library.length ] );
  }

  let narrowest: FieldLayout | null = null;

  for ( const formation of candidates )
  {
    // Deeper variants of the SAME shape before moving on to a different shape:
    // stretching a formation along z lets more of its pairs clear each other in
    // depth instead of across the frame, which narrows it without touching its
    // silhouette. On a tall phone this is what keeps the wider shapes usable
    // instead of leaving only the two narrowest in rotation.
    for ( const stretch of DEPTH_STRETCHES )
    {
      const layout = resolveFieldLayout(
        { ...formation, depth_scale: ( formation.depth_scale ?? 1 ) * stretch },
        claims,
        budget
      );

      if ( layout.required_half_width <= budget )
        return layout;

      if ( narrowest === null || layout.required_half_width < narrowest.required_half_width * ( 1 - MIN_NARROWING ) )
        narrowest = layout;
    }
  }

  return narrowest!;
}

/** Pairs that ended up too close — always empty unless the solver has a bug. */
// Is `ancestor` reached by repeatedly following standOn from `table`? A shelf
// three deep still shares the ground table's footprint, not just its direct
// base's — so a one-hop check would still flag that pair as a real overlap.
function isStandingOn( stands_on: ( number | undefined )[], table: number, ancestor: number ): boolean
{
  let cur: number | undefined = table;

  while ( cur !== undefined )
  {
    if ( cur === ancestor )
      return true;

    cur = stands_on[cur];
  }

  return false;
}

export function layoutOverlaps(
  layout: FieldLayout,
  claims: TableClaim[],
  stands_on?: ( number | undefined )[]
): string[]
{
  const issues: string[] = [];

  for ( let i = 0; i < layout.placements.length; i++ )
    for ( let j = i + 1; j < layout.placements.length; j++ )
    {
      // A shelf shares its whole stack's footprint on purpose — that pair is a
      // stack, not a collision.
      if ( stands_on && ( isStandingOn( stands_on, i, j ) || isStandingOn( stands_on, j, i ) ) )
        continue;

      if ( overlaps( claims[i], claims[j], layout.placements[i], layout.placements[j] ) )
        issues.push( `tables ${i} and ${j} overlap in formation "${layout.formation_id}"` );
    }

  return issues;
}
